import { Icon } from "semantic-ui-react";
import React, { Fragment, useCallback, useContext } from "react";
import styled from "styled-components";
import { ModalContext } from "src/modules/Modal";
import { Contact } from "src/types/Contact";
import { formatOneLineAddress, formatPhoneString } from "src/utils/format";
import { toastError, toastSuccess } from "src/utils/toast";
import { isWrite } from "src/modules/User";
import { DARK_BLUE } from "@120wateraudit/waterworks";

interface Props {
  contact: Contact;
  onDelete: (contact: Contact) => Promise<void>;
}

const ContactDataVersionTwo = ({ contact, onDelete }: Props): JSX.Element => {
  const {
    name,
    pwsContactAddresses: addresses,
    pwsContactCommunications: communications,
    salutation,
  } = contact;
  const number = communications?.find(
    (c) => c.communicationType === "phone"
  )?.communicationValue;
  const fax = communications?.find(
    (c) => c.communicationType === "fax"
  )?.communicationValue;
  const email = communications?.find(
    (c) => c.communicationType === "email"
  )?.communicationValue;
  const web = communications?.find(
    (c) => c.communicationType === "web"
  )?.communicationValue;

  const fullName = salutation ? `${salutation} ${name}` : name;
  return (
    <div style={{ textAlign: "left" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: "1rem" }}>
        <Field>
          <Label>Name:</Label> <Value>{fullName}</Value>
        </Field>
        <Controls
          contact={contact}
          onDelete={async () => await onDelete(contact)}
        />
      </div>
      <Field>
        <Label>Phone:</Label>{" "}
        <Value>{number ? formatPhoneString(number) : "--"}</Value>
      </Field>

      <Field>
        <Label>Email:</Label> <Value>{email ?? "--"}</Value>
      </Field>
      <Field>
        <Label>Fax:</Label> <Value>{fax ?? "--"}</Value>
      </Field>
      <Field>
        <Label>Web:</Label> <Value>{web ?? "--"}</Value>
      </Field>
      {addresses?.map((address, index) => (
        <Field key={address.line1}>
          <Label>{address.addressType} Address:</Label>{" "}
          <Value>{formatOneLineAddress(address) ?? "--"}</Value>
        </Field>
      )) ?? (
        <Field>
          <Label>Contact Address:</Label> <Value>--</Value>
        </Field>
      )}
    </div>
  );
};

interface ControlsProps {
  contact: Contact;
  onDelete: () => Promise<void>;
}

const Controls: React.FC<ControlsProps> = ({ contact, onDelete }) => {
  const { closeModal, openModal } = useContext(ModalContext);
  const confirmDelete = useCallback(async () => {
    try {
      await onDelete();
      toastSuccess("Successfully deleted contact");
    } catch {
      toastError("An error occurred during deletion");
    }
  }, [onDelete]);

  const openDeleteContact = useCallback(
    () =>
      openModal("confirm", {
        body: "This will delete this contact.",
        confirmButtonText: "Delete",
        onConfirm: async () => {
          await confirmDelete();
          closeModal();
        },
      }),
    [openModal, closeModal]
  );

  const openEditContactDrawer = (): void => {
    openModal("editContact", { contact, systemId: contact?.pwsId }, "drawer");
  };

  return (
    <div style={{ display: "flex", gap: ".5rem" }}>
      {isWrite() && (
        <Fragment>
          <Icon
            name="pencil"
            onClick={openEditContactDrawer}
            style={{ color: DARK_BLUE, cursor: "pointer" }}
          />
          <Icon
            name="trash alternate outline"
            onClick={() => openDeleteContact()}
            style={{ color: DARK_BLUE, cursor: "pointer" }}
          />
        </Fragment>
      )}
    </div>
  );
};

const Field = styled.p`
  margin: 0.25rem 0;
  color: #374151;
`;

const Label = styled.span`
  font-weight: 500;
  color: #6b7280;
  text-transform: capitalize;
`;

const Value = styled.span`
  font-weight: 600;
  color: #111827;
`;

export default ContactDataVersionTwo;
